/**
 * KioskAdminPinPanel — let location managers set or clear the per-location
 * kiosk admin PIN. Rendered inside the Kiosks tab of LocationDetail.
 *
 * The PIN is stored as a PBKDF2-SHA256 hash server-side. NULL means
 * "not configured" and the kiosk will show an appropriate message if
 * admin unlock is attempted.
 */

import { useMutation } from "@tanstack/react-query";
import { KeyRound, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import type { Uuid } from "@tilt/admin-sdk";

interface Props {
  locationId: Uuid;
  canWrite: boolean;
}

export function KioskAdminPinPanel({ locationId, canWrite }: Props): JSX.Element {
  const [mode, setMode] = useState<"idle" | "set" | "clear">("idle");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const mutation = useMutation({
    mutationFn: (newPin: string | null) => apiClient.setLocationKioskPin(locationId, newPin),
    onSuccess: (_data, newPin) => {
      setMode("idle");
      setPin("");
      setConfirmPin("");
      setClientError(null);
      setServerError(null);
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current);
      }
      setSuccessMsg(newPin === null ? "Admin PIN cleared." : "Admin PIN updated.");
      successTimerRef.current = setTimeout(() => setSuccessMsg(null), 4000);
    },
    onError: (err) => {
      setServerError(describe(err));
    },
  });

  function handleSetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setClientError(null);
    setServerError(null);
    if (pin.length < 4 || pin.length > 8) {
      setClientError("PIN must be 4 to 8 characters.");
      return;
    }
    if (pin !== confirmPin) {
      setClientError("PINs do not match.");
      return;
    }
    mutation.mutate(pin);
  }

  function handleClearConfirm() {
    setServerError(null);
    mutation.mutate(null);
  }

  function enterSetMode() {
    setMode("set");
    setClientError(null);
    setServerError(null);
    setPin("");
    setConfirmPin("");
  }

  function enterClearMode() {
    setMode("clear");
    setServerError(null);
  }

  function cancelToIdle() {
    setMode("idle");
    setClientError(null);
    setServerError(null);
  }

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <KeyRound className="size-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Kiosk Admin PIN</h3>
      </div>
      <div className="p-4">
        <p className="mb-4 text-sm text-muted-foreground">
          The admin PIN unlocks the on-screen admin panel on kiosk terminals at this location. PINs
          are 4-8 characters and stored as a one-way hash. If no PIN is set, admin access is
          disabled on all kiosks at this location.
        </p>

        {successMsg && (
          <p className="mb-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-emerald-950 dark:text-emerald-300">
            {successMsg}
          </p>
        )}

        {mode === "idle" && (
          <div className="flex gap-3">
            {canWrite && (
              <>
                <button
                  type="button"
                  onClick={enterSetMode}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  Set PIN
                </button>
                <button
                  type="button"
                  onClick={enterClearMode}
                  className="flex items-center gap-1 rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
                >
                  <Trash2 className="size-4" />
                  Clear PIN
                </button>
              </>
            )}
            {!canWrite && (
              <p className="text-sm text-muted-foreground">
                You do not have permission to manage the admin PIN.
              </p>
            )}
          </div>
        )}

        {mode === "set" && (
          <form onSubmit={handleSetSubmit} className="flex flex-col gap-3">
            <div>
              <label
                htmlFor="kiosk-pin-new"
                className="mb-1 block text-sm font-medium text-foreground"
              >
                New PIN
              </label>
              <input
                id="kiosk-pin-new"
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value);
                  setClientError(null);
                }}
                placeholder="4-8 characters"
                maxLength={8}
                autoFocus
                className="w-48 rounded-md border bg-background px-3 py-2 text-sm tracking-widest"
              />
            </div>
            <div>
              <label
                htmlFor="kiosk-pin-confirm"
                className="mb-1 block text-sm font-medium text-foreground"
              >
                Confirm PIN
              </label>
              <input
                id="kiosk-pin-confirm"
                type="password"
                inputMode="numeric"
                value={confirmPin}
                onChange={(e) => {
                  setConfirmPin(e.target.value);
                  setClientError(null);
                }}
                placeholder="Re-enter PIN"
                maxLength={8}
                className="w-48 rounded-md border bg-background px-3 py-2 text-sm tracking-widest"
              />
            </div>
            {clientError && <p className="text-sm text-destructive">{clientError}</p>}
            {serverError && <p className="text-sm text-destructive">{serverError}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {mutation.isPending ? "Saving..." : "Save PIN"}
              </button>
              <button
                type="button"
                onClick={cancelToIdle}
                className="rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {mode === "clear" && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-foreground">
              Clearing the PIN will disable admin access on all kiosks at this location. Are you
              sure?
            </p>
            {serverError && <p className="text-sm text-destructive">{serverError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClearConfirm}
                disabled={mutation.isPending}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
              >
                {mutation.isPending ? "Clearing..." : "Clear PIN"}
              </button>
              <button
                type="button"
                onClick={cancelToIdle}
                className="rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
